import React, { createContext, useContext, useState, useEffect, useMemo, type ReactNode } from 'react'
import { useProfiles } from '../hooks/useProfiles'
import { useAuth } from './AuthContext'
import type { ProfileDocument } from '../types/account'

interface ActiveProfileContextType {
  activeProfile: ProfileDocument | null
  setActiveProfile: (profileId: string) => Promise<void>
  isLoading: boolean
  error: string | null
  // Shared profile data for all consumers
  allProfiles: ProfileDocument[]
  recentProfiles: ProfileDocument[]
  refreshProfiles: () => Promise<void>
}

const ActiveProfileContext = createContext<ActiveProfileContextType | undefined>(undefined)

interface ActiveProfileProviderProps {
  children: ReactNode
}

export const ActiveProfileProvider: React.FC<ActiveProfileProviderProps> = ({ children }) => {
  const [activeProfileId, setActiveProfileId] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  const { allProfiles, recentProfiles, selectProfile, refreshProfiles, isLoading: profilesLoading } = useProfiles()
  const { userAccount } = useAuth()

  // Helper function to find the appropriate default profile based on priority
  const findDefaultProfile = (): string | null => {
    if (allProfiles.length === 0) return null

    // 1. Check if first profile (already sorted by last accessed) has recent access
    const firstProfile = allProfiles[0]
    if (firstProfile.lastViewed || firstProfile.updatedAt) {
      return firstProfile.profileId
    }

    // 2. Check account-level default profile
    if (userAccount?.defaultProfileId) {
      const accountDefault = allProfiles.find(p => p.profileId === userAccount.defaultProfileId)
      if (accountDefault) {
        return accountDefault.profileId
      }
    }

    // 3. Check for profile with default flag
    const profileWithDefaultFlag = allProfiles.find(p => p.default === true)
    if (profileWithDefaultFlag) {
      return profileWithDefaultFlag.profileId
    }

    // 4. Ultimate fallback to first available profile
    return firstProfile.profileId
  }

  // Load active profile from localStorage on mount
  useEffect(() => {
    const savedProfileId = localStorage.getItem('activeProfileId')
    if (savedProfileId) {
      setActiveProfileId(savedProfileId)
    }
    // Don't set isLoading to false here - wait for profiles to load
  }, [])

  // Set active profile when profiles are loaded
  useEffect(() => {
    // Wait for profiles to finish loading
    if (profilesLoading) {
      return
    }

    // Profiles are loaded - now determine active profile
    if (allProfiles.length === 0) {
      // No profiles exist - user needs to create one
      setIsLoading(false)
      return
    }

    // Check if we have a saved profileId from localStorage
    const savedProfileId = localStorage.getItem('activeProfileId')
    
    // Verify that the activeProfileId (if set) actually exists in allProfiles
    const activeProfileExists = activeProfileId && allProfiles.find(p => p.profileId === activeProfileId)
    
    if (savedProfileId && !activeProfileExists) {
      // We have a saved profile - validate it exists
      const profile = allProfiles.find(p => p.profileId === savedProfileId)
      if (profile) {
        // Valid saved profile found - restore it
        setActiveProfileId(savedProfileId)
        setIsLoading(false)
        return
      }
      // Saved profile not found - clear it and fall through to default logic
      localStorage.removeItem('activeProfileId')
    }

    // Only use default profile logic if we don't have a valid activeProfileId
    if (!activeProfileExists) {
      const defaultProfileId = findDefaultProfile()
      if (defaultProfileId) {
        setActiveProfileId(defaultProfileId)
        localStorage.setItem('activeProfileId', defaultProfileId)
      }
    }
    
    // Set loading to false after profiles are loaded and active profile is determined
    setIsLoading(false)
  }, [allProfiles, userAccount, profilesLoading]) // Added profilesLoading; activeProfileId excluded to prevent loops

  const setActiveProfile = async (profileId: string) => {
    try {
      setError(null)
      if (!allProfiles.some(profile => profile.profileId === profileId)) {
        throw new Error('Profile not found or unavailable')
      }
      await selectProfile(profileId)
      setActiveProfileId(profileId)
      localStorage.setItem('activeProfileId', profileId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch profile')
      throw err
    }
  }

  const activeProfile = useMemo(() => {
    return activeProfileId 
      ? allProfiles.find(p => p.profileId === activeProfileId) || null
      : null
  }, [activeProfileId, allProfiles])

  const value: ActiveProfileContextType = useMemo(() => ({
    activeProfile,
    setActiveProfile,
    isLoading,
    error,
    allProfiles,
    recentProfiles,
    refreshProfiles
  }), [activeProfile, setActiveProfile, isLoading, error, allProfiles, recentProfiles, refreshProfiles])

  return (
    <ActiveProfileContext.Provider value={value}>
      {children}
    </ActiveProfileContext.Provider>
  )
}

export const useActiveProfile = (): ActiveProfileContextType => {
  const context = useContext(ActiveProfileContext)
  if (context === undefined) {
    throw new Error('useActiveProfile must be used within an ActiveProfileProvider')
  }
  return context
}
